import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import { useI18n } from "../i18n/i18n";
import { todayYMDLocal } from "../utils/dateUtils";
import {
    TRAINING_FREQUENCIES,
    ROUTINE_SPLITS,
} from "../data/beginnerRoutines";
import { applyRoutineToDateLog } from "../services/workoutPlannerService";
import { pushExerciseLog } from "../services/syncService";
import { useAuthCompat } from "../utils/clerkConfig";
import "./WorkoutPlanner.css";
import "../styles/Common.css";

export default function WorkoutPlanner() {
    const { t, localizePath } = useI18n();
    const navigate = useNavigate();
    const { getToken } = useAuthCompat();

    const [frequencyId, setFrequencyId] = useState("days_3_4");
    const [splitId, setSplitId] = useState("top_bottom");
    const [isGenerating, setIsGenerating] = useState(false);

    const activeFrequency = useMemo(
        () => TRAINING_FREQUENCIES.find((f) => f.id === frequencyId) || TRAINING_FREQUENCIES[1],
        [frequencyId]
    );

    const activeSplit = useMemo(
        () => ROUTINE_SPLITS.find((s) => s.id === splitId) || ROUTINE_SPLITS[0],
        [splitId]
    );

    const recommendedSplits = useMemo(
        () => new Set(activeFrequency.recommendedSplits),
        [activeFrequency]
    );

    const handleFrequencyChange = (nextFrequencyId) => {
        setFrequencyId(nextFrequencyId);
        const freqConfig = TRAINING_FREQUENCIES.find((f) => f.id === nextFrequencyId);
        if (freqConfig && !freqConfig.recommendedSplits.includes(splitId)) {
            setSplitId(freqConfig.defaultSplit);
        }
    };

    const handleSelectFocus = async (focusId) => {
        if (isGenerating) return;
        setIsGenerating(true);
        const today = todayYMDLocal();

        try {
            applyRoutineToDateLog(today, focusId);
        } catch (err) {
            console.error("Failed to apply workout plan:", err);
        }

        try {
            await pushExerciseLog(getToken);
        } catch (_) {
            // best-effort sync; local state is preserved
        }

        navigate(localizePath("/"));
    };

    const flowSteps = [
        { number: t("planner.step1Number"), text: t("planner.step1Text") },
        { number: t("planner.step2Number"), text: t("planner.step2Text") },
        { number: t("planner.step3Number"), text: t("planner.step3Text") },
    ];

    return (
        <div className="plannerPage pageContainer">
            <Breadcrumbs current={t("breadcrumbs.planner")} />

            <header className="plannerHero">
                <h1 className="sectionHeading">{t("planner.title")}</h1>
                <p className="plannerSubtitle">{t("planner.subtitle")}</p>
            </header>

            <div className="plannerStepsWrapper">
                <ExerciseFlowSteps
                    activeStep={3}
                    steps={flowSteps}
                    ariaLabel={t("planner.stepsTitle")}
                />
            </div>

            <main className="plannerContent">
                {/* Step 1: Frequency */}
                <section className="plannerSection" aria-labelledby="planner-freq-heading">
                    <h2 id="planner-freq-heading" className="plannerSectionTitle">
                        {t("planner.questionFrequency")}
                    </h2>
                    <div className="frequencyOptionsGrid" role="radiogroup" aria-labelledby="planner-freq-heading">
                        {TRAINING_FREQUENCIES.map((freq) => {
                            const isSelected = freq.id === frequencyId;
                            return (
                                <button
                                    key={freq.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={isSelected}
                                    className={`plannerOptionCard frequencyCard${isSelected ? " plannerOptionCard--active" : ""}`}
                                    onClick={() => handleFrequencyChange(freq.id)}
                                >
                                    <span className="plannerOptionTitle">{t(freq.labelKey)}</span>
                                    <span className="plannerOptionDesc">{t(freq.descriptionKey)}</span>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Step 2: Split Style */}
                <section className="plannerSection" aria-labelledby="planner-split-heading">
                    <h2 id="planner-split-heading" className="plannerSectionTitle">
                        {t("planner.questionSplit")}
                    </h2>
                    <div className="splitOptionsList" role="radiogroup" aria-labelledby="planner-split-heading">
                        {ROUTINE_SPLITS.map((split) => {
                            const isSelected = split.id === splitId;
                            const isRecommended = recommendedSplits.has(split.id);
                            return (
                                <button
                                    key={split.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={isSelected}
                                    className={`plannerOptionCard splitCard${isSelected ? " plannerOptionCard--active" : ""}`}
                                    onClick={() => setSplitId(split.id)}
                                >
                                    <div className="splitCardHeader">
                                        <span className="plannerOptionTitle">{t(split.nameKey)}</span>
                                        {isRecommended && (
                                            <span className="plannerBadge plannerBadge--recommended">
                                                {t("planner.recommendedBadge")}
                                            </span>
                                        )}
                                    </div>
                                    <span className="plannerOptionDesc">{t(split.descKey)}</span>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Step 3: Today's Focus */}
                <section className="plannerSection" aria-labelledby="planner-focus-heading">
                    <h2 id="planner-focus-heading" className="plannerSectionTitle">
                        {t("planner.questionFocus")}
                    </h2>
                    <p className="plannerNoticeText">{t("planner.generateNotice")}</p>
                    <div className="focusOptionsGrid">
                        {activeSplit.focuses.map((focus) => (
                            <button
                                key={focus.id}
                                type="button"
                                className="plannerFocusCard"
                                onClick={() => handleSelectFocus(focus.id)}
                                disabled={isGenerating}
                            >
                                <span className="plannerFocusTitle">{t(focus.nameKey)}</span>
                                <span className="plannerFocusSubtitle">{t(focus.subtitleKey)}</span>
                                <span className="plannerFocusArrow" aria-hidden="true">→</span>
                            </button>
                        ))}
                    </div>
                </section>

                <div className="plannerFooterActions">
                    <Link to={localizePath("/")} className="plannerBackLink" aria-label={t("planner.backToHome")}>
                        <span aria-hidden="true" className="plannerBackLinkIcon">←</span>
                        <span>{t("planner.backToHome")}</span>
                    </Link>
                </div>
            </main>
        </div>
    );
}
